import {Day, isSameMonth, startOfMonth} from 'date-fns';
import {VStack} from 'platform/foundation';

import {useState} from 'react';

import {TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {Separator} from '../Separator/Separator';
import {CalendarControl} from './components/CalendarControl/CalendarControl';
import {CalendarView} from './components/CalendarView/CalendarView';
import {useCalendarKeyboardNavigation} from './hooks/useCalendarKeyboardNavigation';
import {CalendarFormValue} from './types';

export interface CalendarProps extends FormControlProps<CalendarFormValue>, TestIdProps {
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Array<Date | [Date, Date]>;
  /** disabled days of week, 0 = Sunday */
  disabledWeekdays?: Day[];
  isRange?: boolean;
  hoveredDate?: Date | null;
  onHoverDate?: (date: Date) => void;
}

export function Calendar(props: CalendarProps) {
  const [activeMonth, setActiveMonth] = useState(() =>
    startOfMonth((Array.isArray(props.value) ? props.value[0] : props.value) ?? new Date())
  );

  const [focusedDate, setFocusedDate] = useState<Date | null>(() => {
    const initialDate = Array.isArray(props.value) ? props.value[0] : props.value;
    return initialDate ?? new Date();
  });

  const onSelectDate = (date: CalendarFormValue) => {
    if (!Array.isArray(date) && date && !isSameMonth(date, activeMonth)) {
      setActiveMonth(startOfMonth(date));
    }
    props.onChange?.(date);
  };

  const keyboardHandlers = useCalendarKeyboardNavigation({
    activeMonth,
    focusedDate,
    onFocusChange: setFocusedDate,
    onDateSelect: (date: Date) => onSelectDate(date),
    onMonthChange: setActiveMonth,
    minDate: props.minDate,
    maxDate: props.maxDate,
    disabledDates: props.disabledDates,
  });

  return (
    <div onKeyDown={keyboardHandlers.onKeyDown} tabIndex={0}>
      <VStack>
        <CalendarControl
          onChangeActiveMonth={setActiveMonth}
          activeMonth={activeMonth}
          minDate={props.minDate}
          maxDate={props.maxDate}
          data-testid={props['data-testid']}
        />
        <Separator />
        <CalendarView
          value={props.value}
          onChange={onSelectDate}
          activeMonth={activeMonth}
          minDate={props.minDate}
          maxDate={props.maxDate}
          disabledDates={props.disabledDates}
          disabledWeekdays={props.disabledWeekdays}
          hoveredDate={props.hoveredDate}
          onHoverDate={props.onHoverDate}
          isRange={props.isRange}
          focusedDate={focusedDate}
          onFocusChange={setFocusedDate}
          data-testid={props['data-testid']}
        />
      </VStack>
    </div>
  );
}
